import { c as _c } from "react/compiler-runtime";
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { activitiesApi } from '../api';
import type { VoteData, Activity, ActivityMutationResponse, DeleteMutationResponse } from '../api/types';
import { useObjectActivities, objectActivitiesQueryKey } from './useObjectActivities';

// Hook to fetch user's vote for an object
export function useUserVote(actorId, objectId, objectType) {
  const $ = _c(10);
  const {
    data: t0
  } = useObjectActivities(objectId, objectType);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? [] : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const activities = t1;
  let t2;
  if ($[2] !== activities || $[3] !== actorId) {
    let t3;
    if ($[5] !== actorId) {
      t3 = activity => activity.type === "vote" && String(activity.actorId) === actorId && activity.deletedAt === null;
      $[5] = actorId;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    t2 = activities.find(t3);
    $[2] = activities;
    $[3] = actorId;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const userVote = t2;
  if (!userVote || !userVote.data) {
    let t3;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = {
        data: null
      };
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    return t3;
  }
  const voteData = userVote.data as unknown as VoteData;
  const vote = voteData?.vote as 'up' | 'down' | 'interested' || null;
  let t3;
  if ($[8] !== vote) {
    t3 = {
      data: vote
    };
    $[8] = vote;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}

// Hook to add a vote
export function useAddVote() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp,
      onSuccess: (response_0, variables) => {
        const queryKey = objectActivitiesQueryKey(variables.objectId, variables.objectType);
        queryClient.setQueryData(queryKey, t1 => {
          const old = t1 === undefined ? [] : t1;
          const withoutOldVote = old.filter(_temp2);
          return [...withoutOldVote, response_0.activity];
        });
        if (response_0.engagement) {
          queryClient.setQueryData(["activities", "engagement", variables.objectType, variables.objectId], response_0.engagement);
        }
        if (variables.objectType === "initiative") {
          queryClient.invalidateQueries({
            queryKey: ["initiatives"]
          });
        }
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}

// Hook to remove a vote
function _temp2(activity) {
  return activity.type !== "vote";
}
async function _temp(data) {
  const response = await activitiesApi.addVote({
    ...data,
    includeEngagement: true
  });
  if ("activity" in response) {
    return response as ActivityMutationResponse;
  }
  return {
    activity: response as Activity
  };
}
export function useRemoveVote() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp3,
      onSuccess: result => {
        const queryKey = objectActivitiesQueryKey(result.objectId, result.objectType);
        queryClient.setQueryData(queryKey, t1 => {
          const old = t1 === undefined ? [] : t1;
          return old.filter(activity => {
            if (activity.type !== "vote") {
              return true;
            }
            if (String(activity.actorId) !== result.actorId) {
              return true;
            }
            return false;
          });
        });
        if (result.engagement) {
          queryClient.setQueryData(["activities", "engagement", result.objectType, result.objectId], result.engagement);
        }
        if (result.objectType === "initiative") {
          queryClient.invalidateQueries({
            queryKey: ["initiatives"]
          });
        }
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}

// Hook to change a vote
async function _temp3(data) {
  const response = await activitiesApi.removeVote(data.objectId, data.objectType, true);
  const engagement = response && typeof response === "object" && "engagement" in response ? (response as DeleteMutationResponse).engagement : undefined;
  return {
    ...data,
    engagement
  };
}
export function useChangeVote() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp4,
      onSuccess: (response_0, variables) => {
        const queryKey = objectActivitiesQueryKey(variables.objectId, variables.objectType);
        queryClient.setQueryData(queryKey, t1 => {
          const old = t1 === undefined ? [] : t1;
          const withoutOldVote = old.filter(_temp5);
          return [...withoutOldVote, response_0.activity];
        });
        if (response_0.engagement) {
          queryClient.setQueryData(["activities", "engagement", variables.objectType, variables.objectId], response_0.engagement);
        }
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}
function _temp5(activity) {
  return activity.type !== "vote";
}
async function _temp4(data) {
  const response = await activitiesApi.changeVote({
    ...data,
    includeEngagement: true
  });
  if ("activity" in response) {
    return response as ActivityMutationResponse;
  }
  return {
    activity: response as Activity
  };
}